<template>
  <!-- Change header to Shipment Events -->
  <Section
    :id="id"
    :header="t('events.shipmentEvents', 2)"
    :loading="serviceIncidentTypes.loading || isSaving"
    class="carrier-portal-event-section"
  >
    <ShipmentTimeline
      id="shipment-timeline"
      :shipment-timeline-items="shipmentTimelineItems"
      :tender-requests="tenderRequests"
      :service-incident-types="serviceIncidentTypes.data ?? []"
      :require-both-dates="requireBothArrivalAndDepartureOnStops"
      show-entries-without-locations
      disabled
    />

    <CarrierPortalEventModal
      v-model:visible="eventModal.visible"
      :shipment-event="eventModal.event"
      :event-type-options="shipmentEventTypeOptions"
      :severity-options="shipmentEventSeverityOptions"
      :shipment-stops="shipmentStops"
      @save="onSaveShipmentEvent"
    />

    <ShipmentServiceIncidentModal
      v-model:visible="incidentModal.visible"
      :service-incident="incidentModal.incident"
      :shipment="shipment"
      :types="serviceIncidentTypes.data"
      :severity-options="shipmentEventSeverityOptions"
      :shipment-stops="shipmentStops"
      carrier-disabled
      show-related-stop-dates
      @save="onSaveServiceIncident"
    />
  </Section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { DateTime } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import {
  mapEnumToTranslatedDropdownValue,
  mapEnumToTranslatedDropdownValues,
} from '@rygen/rygen-ui/i18n';
import { Loadable, ServiceIncidentType } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useShipmentTimeLineItems } from '@/composables/useShipmentTimeLineItems';
import {
  Shipment,
  ShipmentEvent,
  ShipmentEventSeverity,
  ShipmentEventType,
  ShipmentServiceIncident,
  ShipmentStop,
  TenderRequest,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import CarrierPortalEventModal from '@/components/carrierPortal/event/CarrierPortalEventModal.vue';
import ShipmentServiceIncidentModal from '@/components/shipment/ShipmentServiceIncidentModal.vue';
import ShipmentTimeline from '@/components/shipment/ShipmentTimeline.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const AVAILABLE_EVENT_TYPES = [
  ShipmentEventType.PICK_UP_ARRIVE,
  ShipmentEventType.PICK_UP_LOAD,
  ShipmentEventType.PICK_UP_DEPART,
  ShipmentEventType.DROPOFF_ARRIVE,
  ShipmentEventType.DROPOFF_UNLOAD,
  ShipmentEventType.DROPOFF_DEPART,
  ShipmentEventType.INFO,
  ShipmentEventType.IN_TRANSIT,
];

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  shipmentEvents: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  shipmentServiceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
  shipmentStops: {
    type: Array as PropType<ShipmentStop[]>,
    default: () => [],
  },
  serviceIncidentTypes: {
    type: Object as PropType<Loadable<ServiceIncidentType[]>>,
    default: () => ({ loading: false }),
  },
  tenderRequests: {
    type: Array as PropType<TenderRequest[]>,
    default: () => [],
  },
});
const {
  id,
  shipment,
  shipmentEvents,
  shipmentServiceIncidents,
  shipmentStops,
  serviceIncidentTypes,
  tenderRequests,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'save', payload: ShipmentEvent | ShipmentServiceIncident): void;
}>();

const { isAuthenticatedUser } = useCurrentUserStore();
const { t } = useI18n();
const router = useRouter();
const shipmentTimeLineItemsUtil = useShipmentTimeLineItems();
const toast = useToast();

const isSaving = ref(false);

const eventModal = ref({
  visible: false,
  event: new ShipmentEvent(),
});

const incidentModal = ref({
  visible: false,
  incident: new ShipmentServiceIncident(),
});

const shipmentEventTypeOptions = AVAILABLE_EVENT_TYPES.map((t) =>
  mapEnumToTranslatedDropdownValue(t, 'ShipmentEventType'),
);

const shipmentEventSeverityOptions = mapEnumToTranslatedDropdownValues(
  ShipmentEventSeverity,
  'ShipmentEventSeverity',
);

const accessToken: Ref<string | undefined> = computed(() => {
  return router.currentRoute.value.query.accessToken as string | undefined;
});

const requireBothArrivalAndDepartureOnStops = computed(
  () => shipment.value.requireBothArrivalAndDepartureOnStops,
);

const shipmentTimelineItems = computed(() => {
  return shipmentTimeLineItemsUtil.toShipmentTimeLineItems(
    shipmentStops.value,
    shipmentEvents.value,
    shipmentServiceIncidents.value,
    tenderRequests.value,
  );
});

const onSaveShipmentEvent = async (shipmentEvent: ShipmentEvent) => {
  if (!shipment.value.id || !accessToken.value) {
    return;
  }

  try {
    isSaving.value = true;

    let savedEvent;
    if (shipmentEvent.id) {
      savedEvent = isAuthenticatedUser.value
        ? await api.shipmentEvent.update(shipment.value.id, shipmentEvent)
        : await api.carrierPortal.updateEvent(
            shipment.value.id,
            shipmentEvent,
            accessToken.value,
          );
    } else {
      savedEvent = isAuthenticatedUser.value
        ? await api.shipmentEvent.create(shipment.value.id, shipmentEvent)
        : await api.carrierPortal.createEvent(
            shipment.value.id,
            shipmentEvent,
            accessToken.value,
          );
    }

    toast.addSuccess({
      detail: t('messages.successSavingParam', { param: t('events.events') }),
    });

    emit('save', savedEvent);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving shipment event', e);
    toast.addError({
      detail: t('errors.savingParam', { param: t('events.events') }),
      error: e,
    });
  } finally {
    isSaving.value = false;
  }
};

const onSaveServiceIncident = async (
  serviceIncident: ShipmentServiceIncident,
) => {
  if (!shipment.value.id || !accessToken.value) {
    return;
  }

  try {
    isSaving.value = true;

    const createdIncident = isAuthenticatedUser.value
      ? await api.shipmentServiceIncident.create(
          shipment.value.id,
          serviceIncident,
        )
      : await api.carrierPortal.createServiceIncident(
          shipment.value.id,
          serviceIncident,
          accessToken.value,
        );

    toast.addSuccess({
      detail: t('messages.successUpdatingParam', {
        param: t('events.incidents'),
      }),
    });

    emit('save', createdIncident);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving service incident', e);
    toast.addError({
      detail: t('errors.savingParam', { param: t('events.incidents') }),
      error: e,
    });
  } finally {
    isSaving.value = false;
  }
};

const onAddEvent = (event: ShipmentEvent) => {
  eventModal.value.event = event;
  eventModal.value.visible = true;
};

const onAddServiceIncident = () => {
  incidentModal.value.incident = new ShipmentServiceIncident({
    severity: ShipmentEventSeverity.WARNING,
    carrierId: shipment.value.carrierId,
    actualDate: DateTime.now(),
  });
  incidentModal.value.visible = true;
};

defineExpose({
  onAddServiceIncident,
  onAddEvent,
});
</script>

<style>
.shipment-event-section {
  .tracking-indicator.p-component {
    color: var(--success-color);
    border-color: var(--success-color);
    cursor: initial;
  }

  .timeline-items {
    .add-button {
      margin-top: var(--sp-3);
    }
  }
}
</style>
